import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { readableTypeName } from '@iotv/core-services';
import { Assembler, BranchingQueryDefinition, DeliveryType, edgeTypeMatrix, Group, IncidentTypeNames, NotificationSubscription, SubscribedStatus, System, SystemClassName, } from '@iotv/datamodel-core';
import MailIcon from '@mui/icons-material/Mail';
import PublicIcon from '@mui/icons-material/Public';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import SmsIcon from '@mui/icons-material/Sms';
import { Alert, Autocomplete, Stack, Tooltip, Typography } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { v4 } from 'uuid';
import { ConsumerDao } from '../../../data/ConsumerDAO';
import { getIconForType } from '../../../Iconography';
import { AdjacentType } from '../../../types';
import { AutosizedPopper } from '../../common/AutosizedPopper';
import { AutosizedTextField } from '../../common/AutosizedTextField';
import { ConfirmButton } from '../../common/ConfirmButton';
import { AlertManagementContext } from './AlertManagementContext';
const debug = process.env.REACT_APP_DEBUG && false;
const NotificationIcon = getIconForType('Notification');
export const SystemRuleset = ({ type, appliesToType }) => {
    const [{ loaded, seedCustomer, contextCustomer, contextUser }] = useContext(AlertManagementContext);
    const [subscription, setSubscription] = useState();
    const deliveryTypes = new Set(subscription?.deliveryPreferences.map((d) => d.deliveryType) ?? []);
    const [processingSubscription, setProcessingSubscription] = useState(false);
    const [processingGroups, setProcessingGroups] = useState(false);
    const [groups, setGroups] = useState([]);
    const [linkedGroup, setLinkedGroup] = useState();
    const fetchSubscription = async () => {
        if (contextUser === undefined)
            return;
        setProcessingSubscription(true);
        const { err, data } = await ConsumerDao.getGraphQueryRequestV2({
            contextObjects: [contextUser],
            query: new BranchingQueryDefinition({
                id: contextUser.sk,
                path: [{ objectTypeId: 'NotificationSubscription', adjacencyType: AdjacentType.CHILD }],
            }),
        });
        if (err || !data)
            return;
        const subscriptions = data.getNodesOfType('NotificationSubscription').map((vob) => new NotificationSubscription(vob));
        debug && console.log('SystemRuleset', 'fetched', subscriptions.length, 'subscriptions');
        const found = subscriptions.find((s) => s.incidentType === type);
        debug && console.log('SystemRuleset', 'found subscription for', found?.incidentSubtype);
        if (!subscription)
            setSubscription(found);
        setProcessingSubscription(false);
    };
    useEffect(() => {
        if (contextCustomer && contextUser)
            fetchSubscription();
    }, [contextCustomer?.sk, contextUser?.sk]);
    const addSubscription = async () => {
        if (!contextUser)
            return;
        setProcessingSubscription(true);
        const newSubscription = new NotificationSubscription({
            id: v4(),
            type: 'NotificationSubscription',
            deliveryPreferences: [
                { deliveryType: DeliveryType.EMAIL, recipients: [contextUser], destinations: [] },
                { deliveryType: DeliveryType.MOBILE_PUSH, recipients: [contextUser], destinations: [] },
            ],
            filter: {
                severities: [],
                statuses: [],
                onActions: [],
            },
            incidentEntityOrGroupingSk: appliesToType === SystemClassName ? new System({}).sk : appliesToType + ':*',
            incidentInitiatorSk: undefined,
            incidentProducingTypeId: appliesToType,
            incidentType: type,
            incidentSubtype: undefined,
            subscriptionState: {
                status: SubscribedStatus.ACTIVE,
            },
        });
        const { err, data } = await ConsumerDao.createAndLink({
            existingObject: contextUser,
            newObject: newSubscription,
            edgeTypeId: edgeTypeMatrix.User.NotificationSubscription.User_has_NotificationSubscription.edgeTypeId,
        });
        debug && console.log('SystemRuleset', err, data);
        if (err || !data)
            return;
        const [source, edge, target] = data;
        if (target)
            setSubscription(newSubscription);
        setProcessingSubscription(false);
    };
    const removeSubscription = async () => {
        if (!contextUser || !subscription)
            return;
        setProcessingSubscription(true);
        const { err, data } = await ConsumerDao.deleteObject(subscription);
        debug && console.log('SystemRuleset', err, data);
        if (err || !data)
            return;
        setSubscription(undefined);
        setProcessingSubscription(false);
    };
    const updateSubscriptionTarget = async (group) => {
        if (!subscription || processingSubscription || processingGroups)
            return;
        setProcessingGroups(true);
        setProcessingSubscription(true);
        debug && console.log('SystemRuleset', 'updating subscription target');
        if (group === undefined) {
            // make global
            const sk = appliesToType + ':*';
            if (subscription.incidentEntityOrGroupingSk === sk) {
                setProcessingSubscription(false);
                setProcessingGroups(false);
                return;
            }
            const newSubscription = new NotificationSubscription(subscription);
            newSubscription.incidentProducingTypeId = appliesToType ?? newSubscription.incidentProducingTypeId;
            newSubscription.incidentEntityOrGroupingSk = sk;
            const { err, data } = await ConsumerDao.save(newSubscription);
            debug && console.log('SystemRuleset', err, data);
            if (err || !data) {
                setProcessingSubscription(false);
                setProcessingGroups(false);
                return;
            }
            setSubscription(newSubscription);
        }
        else {
            // make specific
            const name = group.name;
            const type = group.type;
            const sk = group.sk;
            if (subscription.incidentEntityOrGroupingSk === sk) {
                setProcessingSubscription(false);
                setProcessingGroups(false);
                return;
            }
            const newSubscription = new NotificationSubscription(subscription);
            newSubscription.incidentEntityOrGroupingName = name ?? newSubscription.incidentEntityOrGroupingName;
            newSubscription.incidentProducingTypeId = type ?? newSubscription.incidentProducingTypeId;
            newSubscription.incidentEntityOrGroupingSk = sk;
            const { err, data } = await ConsumerDao.save(newSubscription);
            debug && console.log('SystemRuleset', err, data);
            if (err || !data) {
                setProcessingSubscription(false);
                setProcessingGroups(false);
                return;
            }
            setSubscription(newSubscription);
        }
        setProcessingSubscription(false);
        setProcessingGroups(false);
    };
    const fetchGroups = async () => {
        if (contextCustomer === undefined)
            return;
        setProcessingGroups(true);
        // fetch all groups for the entity type
        let items = [];
        const allGroups = await ConsumerDao.getGraphQueryRequestV2({
            contextObjects: [contextCustomer],
            query: new BranchingQueryDefinition({
                id: contextCustomer.sk,
                path: [{ objectTypeId: 'Group', adjacencyType: AdjacentType.CHILD }],
            }),
        });
        if (allGroups.err)
            return setProcessingGroups(false);
        items =
            allGroups.data
                ?.getNodesOfType('Group')
                ?.filter((group) => group && group.supportedType === appliesToType)
                .map((group) => Assembler.getInstance(group))
                .filter((group) => group !== undefined) ?? [];
        setGroups(items);
        // fetch linked group
        if (appliesToType !== SystemClassName && subscription) {
            const { err, data } = await ConsumerDao.getExact(new Group({ pk: subscription.incidentEntityOrGroupingSk, sk: subscription.incidentEntityOrGroupingSk, type: 'Group', supportedType: appliesToType }));
            if (err)
                return setProcessingGroups(false);
            if (data?.Items)
                setLinkedGroup(data.Items[0]);
        }
        setProcessingGroups(false);
    };
    useEffect(() => {
        if (appliesToType !== SystemClassName)
            fetchGroups();
    }, [subscription?.sk, appliesToType]);
    return (_jsxs(Stack, { padding: 1, direction: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 1, sx: { borderRadius: 1, border: (t) => `1px solid ${t.palette.divider}` }, children: [_jsx(PublicIcon, { color: "primary", sx: { fontSize: '2em' } }), _jsx(Typography, { variant: "h6", children: IncidentTypeNames[type] }), subscription ? (_jsxs(_Fragment, { children: [appliesToType !== SystemClassName && (_jsx(Autocomplete, { value: linkedGroup?.sk ?? null, freeSolo: false, options: groups.sort((a, b) => a?.name?.localeCompare(b?.name ?? '') ?? 0).map((group) => group.sk), getOptionLabel: (option) => groups.filter((group) => group.sk === option).shift()?.name ?? option, onChange: (_, value) => {
                            const group = groups.find((group) => group.sk === value);
                            setLinkedGroup(group);
                            updateSubscriptionTarget(group);
                        }, loading: processingGroups, disabled: processingSubscription || processingGroups || (!subscription.incidentEntityOrGroupingSk.includes(':*') && linkedGroup === undefined), size: "small", slots: { popper: AutosizedPopper }, renderInput: (params) => (_jsx(AutosizedTextField, { ...params, label: `${readableTypeName(appliesToType)} Groups`, error: seedCustomer?.sk !== contextCustomer?.sk &&
                                linkedGroup === undefined &&
                                !processingGroups &&
                                !processingSubscription &&
                                subscription.incidentEntityOrGroupingSk.includes(':*') })) })), subscription.incidentEntityOrGroupingSk.includes(':*') && (_jsx(Alert, { severity: seedCustomer?.sk !== contextCustomer?.sk ? 'error' : 'warning', sx: { paddingBlock: 0, paddingInlineEnd: 1, alignItems: 'center' }, children: seedCustomer?.sk !== contextCustomer?.sk
                            ? 'Please select one or more groups!'
                            : `You are currently subscribed to every ${readableTypeName(appliesToType)} in the system!` })), _jsxs(Stack, { direction: 'row', alignItems: 'center', gap: 1, children: [deliveryTypes.has(DeliveryType.EMAIL) ? (_jsx(Tooltip, { title: "Email notification", arrow: true, children: _jsx(MailIcon, {}) })) : (_jsx(_Fragment, {})), deliveryTypes.has(DeliveryType.MOBILE_PUSH) ? (_jsx(Tooltip, { title: "Push notification", arrow: true, children: _jsx(SmartphoneIcon, {}) })) : (_jsx(_Fragment, {})), deliveryTypes.has(DeliveryType.SMS) ? (_jsx(Tooltip, { title: "SMS notification", arrow: true, children: _jsx(SmsIcon, {}) })) : (_jsx(_Fragment, {}))] }), _jsx(ConfirmButton, { color: "error", startIcon: _jsx(NotificationIcon, {}), confirmMessage: "Unsubscribe?", onConfirm: removeSubscription, loading: processingSubscription, disabled: !loaded || processingSubscription || processingGroups, children: "Unsubscribe" })] })) : (_jsx(ConfirmButton, { color: "success", startIcon: _jsx(NotificationIcon, {}), confirmMessage: "Subscribe?", onConfirm: addSubscription, loading: processingSubscription, disabled: !loaded || processingSubscription || processingGroups, children: "Subscribe" }))] }));
};
